"use client";
/*
 * Documentation:
 * Avatar — https://app.subframe.com/d960199b9abf/library?component=Avatar_bec25ae6-5010-4485-b46b-cf79e3943ab2
 * Default Page Layout — https://app.subframe.com/d960199b9abf/library?component=Default+Page+Layout_a57b1c43-310a-493f-b807-8cc88e2452cf
 * Dropdown Menu — https://app.subframe.com/d960199b9abf/library?component=Dropdown+Menu_99951515-459b-4286-919e-a89e7549b43b
 * Icon Button — https://app.subframe.com/d960199b9abf/library?component=Icon+Button_af9405b1-8c54-4e01-9786-5aad308224f6
 * Sidebar with sections — https://app.subframe.com/d960199b9abf/library?component=Sidebar+with+sections_f4047c8b-cfb4-4761-b9cf-fbcae8a9b9b5
 */

import React, { useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router";
import { FeatherBell } from "@subframe/core";
import { FeatherHome } from "@subframe/core";
import { FeatherZap } from "@subframe/core";
import { FeatherGrid } from "@subframe/core";
import { FeatherAward } from "@subframe/core";
import { FeatherLayers } from "@subframe/core";
import { FeatherLogOut } from "@subframe/core";
import { FeatherLogIn } from "@subframe/core";
import { FeatherSettings } from "@subframe/core";
import { FeatherWallet } from "@subframe/core";
import { FeatherUsers } from "@subframe/core";
import { FeatherMessageCircle } from "@subframe/core";
import { FeatherRefreshCw } from "@subframe/core";
import { FeatherArrowLeftRight } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherLayout } from "@subframe/core";
import { FeatherSend } from "@subframe/core";
import { FeatherDroplet } from "@subframe/core";
import { FeatherShoppingCart } from "@subframe/core";
import { FeatherStar } from "@subframe/core";
import { FeatherCopy } from "@subframe/core";
import { FeatherCheck } from "@subframe/core";
import { SidebarWithSections } from "../components/SidebarWithSections";
import * as SubframeUtils from "../utils";
import { useAuthStore } from "../../store/authStore";
import { useNotificationStore } from "../../store/notificationStore";
import { logout as apiLogout } from "../../api/auth";
import { apiClient } from "../../api/client";
import { formatAddress } from "../../utils/formatters";
import { MobileNav } from "../../components/MobileNav";
import { useUnreadCount } from "../../hooks/useUnreadCount";
import { useUnreadMessageCount } from "../../hooks/useMessages";
import { usePendingTradeCount } from "../../hooks/usePendingTradeCount";
import { useNewOfferCount } from "../../hooks/useNewOfferCount";
import { useXamanLogin } from "../../hooks/useXamanLogin";
import { useSignInModalStore } from "../../store/signInModalStore";
import { WalletPickerModal } from "../../components/auth/WalletPickerModal";
import { XamanModal } from "../../components/auth/XamanModal";
import { SignInBanner } from "../../components/SignInBanner";
import { MigrateBanner } from "../../components/MigrateBanner";
import { GlobalSearchBar } from "../../components/GlobalSearchBar";
import { AnimatedStar } from "../../components/ui/AnimatedStar";
import { WalletMenu } from "../../components/WalletMenu";
import { networkConfig } from "../../config/network";
import { featureFlags } from "../../config/featureFlags";
import { useCartStore } from "../../store/cartStore";
import { useCartCheckout } from "../../hooks/useCartCheckout";
import { CartDrawer } from "../../components/cart/CartDrawer";
import { CartCheckoutModal } from "../../components/cart/CartCheckoutModal";

interface DefaultPageLayoutRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

const DefaultPageLayoutRoot = React.forwardRef<
  HTMLDivElement,
  DefaultPageLayoutRootProps
>(function DefaultPageLayoutRoot(
  { children, className, ...otherProps }: DefaultPageLayoutRootProps,
  ref
) {
  const { user, logout, updateUser, isAuthenticated } = useAuthStore();
  const { success: showSuccess } = useNotificationStore();
  const { unreadCount } = useUnreadCount({ enablePolling: isAuthenticated });
  const { unreadCount: unreadMessageCount } = useUnreadMessageCount({ enablePolling: isAuthenticated });
  const { count: pendingTradeCount } = usePendingTradeCount({ enablePolling: isAuthenticated });
  const { newOfferCount } = useNewOfferCount({ enablePolling: isAuthenticated });
  const navigate = useNavigate();
  const location = useLocation();
  const displayAddress = user?.xrpl_address ? formatAddress(user.xrpl_address) : 'Not connected';
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [migrateBannerDismissed, setMigrateBannerDismissed] = useState(() => {
    const dismissed = localStorage.getItem('migrate_banner_dismissed_at');
    if (!dismissed) return false;
    const hoursSince = (Date.now() - Number(dismissed)) / (1000 * 60 * 60);
    return hoursSince < 72;
  });
  const { showModal, xamanData, mode, initiateLogin, initiateLink, closeModal, handleSuccess } = useXamanLogin();
  // Global sign-in modal: every "Sign In" / "Connect Wallet" trigger app-wide opens
  // this one wallet picker (Xaman + Joey) via useSignInModalStore.
  const signInModalOpen = useSignInModalStore((s) => s.isOpen);
  const closeSignInModal = useSignInModalStore((s) => s.close);
  const [addressCopied, setAddressCopied] = useState(false);

  const handleCopyAddress = useCallback(() => {
    if (!user?.xrpl_address) return;
    navigator.clipboard.writeText(user.xrpl_address);
    setAddressCopied(true);
    setTimeout(() => setAddressCopied(false), 2000);
  }, [user?.xrpl_address]);

  // Cart state
  const cartStore = useCartStore();
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const cartCheckout = useCartCheckout();

  const handleCartCheckout = () => {
    setCartDrawerOpen(false);
    cartCheckout.startConfirmation(cartStore.items);
    setCheckoutModalOpen(true);
  };

  const handleCheckoutClose = () => {
    if (cartCheckout.state.stage !== 'purchasing') {
      cartCheckout.reset();
      setCheckoutModalOpen(false);
    }
  };

  const handleCheckoutComplete = () => {
    cartCheckout.completeCheckout();
    setCheckoutModalOpen(false);
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
      logout();
      showSuccess('Successfully logged out');
      navigate('/');
    } catch (err) {
      // Logout locally even if API call fails
      logout();
      showSuccess('Logged out');
      navigate('/');
    }
  };

  return (
    <div
      className={SubframeUtils.twClassNames(
        "flex min-h-screen w-full flex-col mobile:flex-col",
        className
      )}
      ref={ref}
      {...otherProps}
    >
      {/* Testnet Warning Banner - temporarily disabled, re-enable when testnet support is ready */}
      {/* {networkConfig.isTestnet && (
        <div className="bg-warning-50 text-warning-700 text-center py-2 px-4 text-sm font-medium border-b border-warning-200 sticky top-0 z-50">
          TESTNET MODE - Transactions use test XRP with no real value
        </div>
      )} */}

      {/* Mobile Header */}
      <div className="hidden mobile:flex items-center justify-between px-4 py-3 border-b border-solid border-neutral-border bg-default-background sticky top-0 z-30">
        <MobileNav />
        <Link to="/">
          <img
            className="h-8 flex-none object-contain"
            src="/logo-transparent.png"
            alt="XRP Deals"
          />
        </Link>
        <div className="flex items-center gap-1">
          <GlobalSearchBar mobileCollapsible placeholder="Search..." />
          {featureFlags.shoppingCart && isAuthenticated && (
            <button
              onClick={() => setCartDrawerOpen(true)}
              className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-neutral-100 transition-colors"
              aria-label="Shopping cart"
            >
              <FeatherShoppingCart className="h-5 w-5 text-default-font" />
              {cartStore.items.length > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[1.25rem]">
                  {cartStore.items.length > 99 ? '99+' : cartStore.items.length}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-1 items-start">
        <SidebarWithSections
          className="mobile:hidden"
          header={
            <Link to="/">
              <img
                className="h-40 flex-none object-contain"
                src="/logo-transparent.png"
                alt="XRP Deals"
              />
            </Link>
          }
          footer={
            isAuthenticated ? (
              <div className="flex grow shrink-0 basis-0 items-center justify-between gap-2 w-full">
                <button
                  onClick={handleCopyAddress}
                  className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-none p-0"
                  title="Copy wallet address"
                >
                  {addressCopied ? (
                    <FeatherCheck className="text-body font-body text-green-500" />
                  ) : (
                    <FeatherCopy className="text-body font-body text-subtext-color" />
                  )}
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    {displayAddress}
                  </span>
                </button>
                <WalletMenu onConnectWallet={initiateLink} />
              </div>
            ) : null
          }
        >
          <Link to="/" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherHome />} selected={location.pathname === '/'}>
              Home
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/deals" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherZap />} selected={location.pathname === '/deals'}>
              Deals
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/projects" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherLayers />} selected={location.pathname.startsWith('/projects')}>
              Projects
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/collections" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherGrid />} selected={location.pathname === '/collections'}>
              Collections
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/drops" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherDroplet />} selected={location.pathname.startsWith('/drops')}>
              Drops
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/flip" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherRefreshCw />} selected={location.pathname === '/flip'}>
              Flip NFTs
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/leaderboard" className="w-full">
            <SidebarWithSections.NavItem icon={<FeatherAward />} selected={location.pathname === '/leaderboard'}>
              Leaderboard
            </SidebarWithSections.NavItem>
          </Link>
          <Link to="/ambassadors" className="w-full">
            <SidebarWithSections.NavItem icon={<AnimatedStar className="h-5 w-5" />} selected={location.pathname === '/ambassadors'}>
              Ambassadors
            </SidebarWithSections.NavItem>
          </Link>
          {isAuthenticated ? (
            <>
            <SidebarWithSections.NavSection label="Creator">
              <Link to="/creator" className="w-full">
                <SidebarWithSections.NavItem icon={<FeatherLayout />} selected={location.pathname.startsWith('/creator')}>
                  Dashboard
                </SidebarWithSections.NavItem>
              </Link>
              <Link to="/launchpad" className="w-full">
                <SidebarWithSections.NavItem icon={<FeatherSend />} selected={location.pathname.startsWith('/launchpad')}>
                  Launchpad
                </SidebarWithSections.NavItem>
              </Link>
            </SidebarWithSections.NavSection>
            <SidebarWithSections.NavSection label="My Account">
              {user?.xrpl_address && (
                <Link to={`/wallets/${user.xrpl_address}${newOfferCount > 0 ? '?tab=offers' : ''}`} className="w-full">
                  <SidebarWithSections.NavItem
                    icon={<FeatherWallet />}
                    selected={location.pathname === `/wallets/${user.xrpl_address}`}
                    rightSlot={newOfferCount > 0 ? (
                      <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-amber-500 rounded-full min-w-[1.25rem]">
                        {newOfferCount > 99 ? '99+' : newOfferCount}
                      </span>
                    ) : null}
                  >
                    My Wallet
                  </SidebarWithSections.NavItem>
                </Link>
              )}
              <Link to="/inbox" className="w-full">
                <SidebarWithSections.NavItem
                  icon={<FeatherMessageCircle />}
                  selected={location.pathname === '/inbox'}
                  rightSlot={unreadMessageCount > 0 ? (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-blue-500 rounded-full min-w-[1.25rem]">
                      {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                    </span>
                  ) : null}
                >
                  Messages
                </SidebarWithSections.NavItem>
              </Link>
              <Link to="/notifications" className="w-full">
                <SidebarWithSections.NavItem
                  icon={<FeatherBell />}
                  selected={location.pathname === '/notifications'}
                  rightSlot={unreadCount > 0 ? (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium text-neutral-500 bg-default-background border border-neutral-800 rounded-full min-w-[1.25rem]">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  ) : null}
                >
                  Notifications
                </SidebarWithSections.NavItem>
              </Link>
              <Link to="/trades" className="w-full">
                <SidebarWithSections.NavItem
                  icon={<FeatherArrowLeftRight />}
                  selected={location.pathname === '/trades'}
                  rightSlot={pendingTradeCount > 0 ? (
                    <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-brand-600 rounded-full min-w-[1.25rem]">
                      {pendingTradeCount > 99 ? '99+' : pendingTradeCount}
                    </span>
                  ) : null}
                >
                  Trades
                </SidebarWithSections.NavItem>
              </Link>
              <Link to="/referrals" className="w-full">
                <SidebarWithSections.NavItem icon={<FeatherUsers />} selected={location.pathname === '/referrals'}>
                  Referrals
                </SidebarWithSections.NavItem>
              </Link>
              <Link to="/settings" className="w-full">
                <SidebarWithSections.NavItem icon={<FeatherSettings />} selected={location.pathname === '/settings'}>
                  Settings
                </SidebarWithSections.NavItem>
              </Link>
              <SidebarWithSections.NavItem icon={<FeatherLogOut />} onClick={handleLogout}>
                Logout
              </SidebarWithSections.NavItem>
            </SidebarWithSections.NavSection>
            </>
          ) : (
            <SidebarWithSections.NavItem icon={<FeatherLogIn />} onClick={() => useSignInModalStore.getState().open()}>
              Sign In
            </SidebarWithSections.NavItem>
          )}
        </SidebarWithSections>
        <div className="flex grow shrink basis-0 flex-col items-start self-stretch bg-default-background min-w-0">
          {/* Desktop Search Bar + Cart */}
          <div className="w-full max-w-[1400px] pt-6 px-6 mobile:hidden flex items-center gap-3">
            <div className="flex-1" />
            <GlobalSearchBar />
            <div className="flex-1 flex justify-end">
              {featureFlags.shoppingCart && isAuthenticated && (
                <button
                  onClick={() => setCartDrawerOpen(true)}
                  className="relative flex items-center justify-center w-10 h-10 rounded-md hover:bg-neutral-100 transition-colors flex-shrink-0"
                  aria-label="Shopping cart"
                >
                  <FeatherShoppingCart className="h-5 w-5 text-default-font" />
                  {cartStore.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white bg-red-500 rounded-full min-w-[1.25rem]">
                      {cartStore.items.length > 99 ? '99+' : cartStore.items.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
          {/* Sign-in banner for logged-out users */}
          {!isAuthenticated && !bannerDismissed && (
            <SignInBanner onDismiss={() => setBannerDismissed(true)} />
          )}
          {/* Migrate listings banner for users with offers on other marketplaces */}
          {isAuthenticated && user?.show_migrate_banner && !migrateBannerDismissed && (
            <MigrateBanner onDismiss={() => {
              setMigrateBannerDismissed(true);
              localStorage.setItem('migrate_banner_dismissed_at', String(Date.now()));
            }} />
          )}
          {children ? (
            <div className="w-full max-w-[1400px] py-8 px-6 mobile:py-4 mobile:px-4">
              {children}
            </div>
          ) : null}
        </div>
      </div>

      {/* Single app-wide wallet picker (Xaman + Joey), opened by any sign-in trigger */}
      <WalletPickerModal
        open={signInModalOpen}
        onClose={closeSignInModal}
        onXaman={() => {
          closeSignInModal();
          initiateLogin();
        }}
      />
      {showModal && (
        <XamanModal
          uuid={xamanData.uuid}
          qrCode={xamanData.qrCode}
          deepLink={xamanData.deepLink}
          onClose={closeModal}
          onSuccess={handleSuccess}
          mode={mode}
        />
      )}

      {/* Cart Drawer */}
      {featureFlags.shoppingCart && (
        <CartDrawer
          open={cartDrawerOpen}
          onClose={() => setCartDrawerOpen(false)}
          onCheckout={handleCartCheckout}
        />
      )}

      {/* Cart Checkout Modal */}
      {featureFlags.shoppingCart && (
        <CartCheckoutModal
          open={checkoutModalOpen}
          onClose={handleCheckoutClose}
          items={cartCheckout.itemsToCheckout}
          state={cartCheckout.state}
          totals={cartCheckout.totals}
          onStartPurchase={cartCheckout.startPurchasing}
          onCancelConfirmation={() => {
            cartCheckout.reset();
            setCheckoutModalOpen(false);
          }}
          onCancelRemaining={cartCheckout.cancelRemaining}
          onResolveClientItem={cartCheckout.resolveClientItem}
          onComplete={handleCheckoutComplete}
        />
      )}
    </div>
  );
});

export const DefaultPageLayout = DefaultPageLayoutRoot;
